import React, { useMemo } from "react";

import { groupBy } from "lodash";
import { compareAsc } from "date-fns";

import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Wrap,
  Tag,
  Flex,
  Button,
} from "@chakra-ui/react";
import CheckboxCardGroup from "../../../utils/chakra/CheckboxCardGroup";
import RadioCardGroup from "../../../utils/chakra/RadioCardGroup";

import {
  AnswerFragment,
  AnswerHistoryFragment,
  QuestionFragment,
  QuestionTypeEnum,
  ReportFragment,
  useGetAnswersHistory,
} from "../../../../graphql/generated/definitions";

import useTranslations from "../../../../hooks/useTranslations";
import useDateFnsLocale from "../../../../hooks/useDateFnsLocale";
import { dateFormatter } from "../../../../hooks/useFormatDate";

import AnswerReviewDispatch from "./AnswerReviewDispatch";

const is = (q: QuestionFragment, t: QuestionTypeEnum) => q.type === t;

const AnswersByReport: React.FC<{
  answers: AnswerHistoryFragment[];
  report: ReportFragment;
  question: QuestionFragment;
}> = ({ answers, report, question }) => {
  const t = useTranslations();
  const dateFnsLocale = useDateFnsLocale();

  const collaboratorAnswer = useMemo(
    () => answers.find((answer) => answer.isCollaborator),
    [answers, report]
  );

  const referentAnswers = useMemo(
    () =>
      answers
        .filter((answer) => !answer.isCollaborator)
        .sort((answer1, answer2) =>
          compareAsc(new Date(answer1.createdAt), new Date(answer2.createdAt))
        ),
    [answers, report]
  );

  const reportDate = report.createdAt;

  return (
    <Stack spacing="8">
      <Wrap>
        <Tag variant="solid" colorScheme="cyan" size="lg">
          {t("reports.reportFrom", {
            date: dateFormatter(
              reportDate,
              t("dateFormat.short") as string,
              t("dateFormat.fallback") as string,
              dateFnsLocale
            ),
          })}
        </Tag>
      </Wrap>
      {collaboratorAnswer && (
        <AnswerReviewDispatch
          answer={collaboratorAnswer as AnswerFragment}
          question={question}
          path={"history"}
        />
      )}
      {referentAnswers.map((referentAnswer) => (
        <AnswerReviewDispatch
          key={referentAnswer.id}
          answer={referentAnswer as AnswerFragment}
          question={question}
          path={"history"}
        />
      ))}
    </Stack>
  );
};

type AnswerHistoryProps = {
  open: boolean;
  onClose: VoidFunction;
  question: QuestionFragment;
  report: ReportFragment;
};

const AnswerHistory: React.FC<AnswerHistoryProps> = ({
  open,
  onClose,
  question,
  report,
}) => {
  const t = useTranslations();

  const { data } = useGetAnswersHistory({
    variables: {
      questionDigest: [question.digest],
      reviewId: report.review.id,
      direction: "DESC",
      sortBy: "createdAt",
    },
  });

  const answers = groupBy(data?.answers?.collection, "reportId");
  const isEmpty = Object.keys(answers).length === 0;

  return (
    <Modal onClose={onClose} isOpen={open} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent px="16" py="12">
        <ModalHeader>{t("reports.answerHistory")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="8">
            <Stack spacing="3" bgColor="primary.50" px="5" py="6">
              <Text fontSize="md" fontWeight="medium">
                {question.label}
              </Text>
              {question.description && (
                <Text fontSize="sm" color="gray.600">
                  {question.description}
                </Text>
              )}
              {is(question, "QuestionSelectorMultiple") && (
                <CheckboxCardGroup
                  isInvalid={false}
                  isDisabled
                  options={question.options}
                />
              )}
              {!is(question, "QuestionSelectorMultiple") &&
                !is(question, "QuestionText") && (
                  <RadioCardGroup isDisabled options={question.options} />
                )}
            </Stack>
            <Stack spacing="16">
              {Object.keys(answers).map((reportId) => {
                return (
                  <AnswersByReport
                    key={reportId}
                    question={question}
                    answers={answers[reportId]}
                    report={report}
                  />
                );
              })}
              {isEmpty && (
                <Flex direction="column" alignItems="center" pt="12">
                  <Text color="#E39640" fontSize="xl" fontWeight="bold">
                    {t("reports.shared.noAnswersYet")}
                  </Text>
                  <Text color="gray.600" pt="4">
                    {t("reports.shared.thereforeNoAnswers")}
                  </Text>
                  <Box>
                    <Button colorScheme="primary" onClick={onClose} mt="14">
                      {t("reports.shared.close")}
                    </Button>
                  </Box>
                </Flex>
              )}
            </Stack>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AnswerHistory;
